<div class="sketch-browser">
  <!-- Header -->
  <div class="sketches-header">
    <span class="sketches-header__title">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
      Sketches
    </span>
    <span class="sketches-header__count">({{ treeNodes.length }})</span>
    <!-- Done button when in sketch mode -->
    @if (isSketchMode) {
      <button class="finish-sketch-btn" (click)="onFinishEditing.emit()" appTooltip="Finish Sketch (Esc)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Done
      </button>
    }
    <!-- Add button when not in sketch mode -->
    @if (!isSketchMode) {
      <button class="add-sketch-btn" (click)="onCreateSketch()" appTooltip="New Sketch (N)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    }
  </div>

  <!-- Sketches List -->
  @if (treeNodes.length > 0) {
    <div class="sketches-list">
      @for (node of treeNodes; track node) {
        <ng-container *ngTemplateOutlet="treeNodeTemplate; context: { node: node, depth: 0 }">
        </ng-container>
      }
    </div>
  }

  <!-- Empty State -->
  @if (treeNodes.length === 0) {
    <div class="sketches-empty">
      <div class="sketches-empty__title">No sketches</div>
      <div class="sketches-empty__subtitle">Create a sketch to start drawing 2D shapes.</div>
    </div>
  }

  <!-- Tree Node Template -->
  <ng-template #treeNodeTemplate let-node="node" let-depth="depth">
    <div class="sketches-list__item"
      [class.sketches-list__item--selected]="node.isSelected"
      [class.sketches-list__item--active]="isNodeActive(node)"
      [class.sketches-list__item--hovered]="hoveredNodeId === node.id"
      [class.sketches-list__item--hidden]="!node.isVisible"
      [class.sketches-list__item--sketch]="node.type === 'sketch'"
      [class.sketches-list__item--folder]="node.type === 'folder'"
      [class.sketches-list__item--entity]="node.type === 'entity'"
      [class.sketches-list__item--profile]="node.type === 'profile'"
      [class.sketches-list__item--construction]="node.type === 'entity' && node.metadata?.isConstruction"
      [style.margin-left.px]="depth * 12"
      (click)="onNodeClick(node, $event)"
      (dblclick)="onNodeDoubleClick(node)"
      (contextmenu)="onNodeContextMenu(node, $event)"
      (mouseenter)="onNodeHover(node)"
      (mouseleave)="onNodeHover(null)">

      <div class="sketches-list__info">
        <!-- Expand/Collapse Toggle -->
        @if (node.children.length > 0) {
          <button class="expand-btn" (click)="toggleExpand(node, $event)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              [class.expanded]="node.isExpanded">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        }
        @if (node.children.length === 0) {
          <span class="expand-spacer"></span>
        }

        <!-- Icon -->
        <div class="sketches-list__icon" [class]="'type-' + node.type">
          @switch (node.type) {
            @case ('sketch') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
              </svg>
            }
            @case ('folder') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            }
            @case ('profile') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
              </svg>
            }
            @default {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
              </svg>
            }
          }
        </div>

        <div class="sketches-list__name-container">
          <!-- Name -->
          @if (!node.isEditing) {
            <span class="sketches-list__name">{{ node.name }}</span>
          }
          @if (node.isEditing) {
            <input class="sketches-list__name-input" [(ngModel)]="editingName"
              (blur)="finishEditing(node)" (keydown.enter)="finishEditing(node)"
              (keydown.escape)="cancelEditing(node)" (click)="$event.stopPropagation()">
          }
        </div>
      </div>

      <div class="sketches-list__actions">
        <!-- Construction badge for entities -->
        @if (node.type === 'entity' && node.metadata?.isConstruction) {
          <span class="construction-badge"
            appTooltip="Construction geometry">
            C
          </span>
        }

        <!-- Area badge for profiles -->
        @if (node.type === 'profile' && node.metadata?.area) {
          <span class="area-badge">
            {{ node.metadata.area | unit:'area':unitSystem() }}
          </span>
        }

        <!-- Visibility toggle for sketches -->
        @if (node.type === 'sketch') {
          <button class="action-btn visibility-btn"
            [class.hidden]="!node.isVisible"
            (click)="toggleVisibility(node, $event)"
            [appTooltip]="node.isVisible ? 'Hide' : 'Show'">
            @if (node.isVisible) {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
            }
            @if (!node.isVisible) {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            }
          </button>
        }

        <!-- Delete Button -->
        @if (node.type === 'entity' || node.type === 'sketch') {
          <button class="action-btn delete-btn"
            (click)="onDeleteNode(node, $event)" appTooltip="Delete">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path
                d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- Children -->
    @if (node.isExpanded && node.children.length > 0) {
      <div class="sketches-children">
        @for (child of node.children; track child) {
          <ng-container *ngTemplateOutlet="treeNodeTemplate; context: { node: child, depth: depth + 1 }">
          </ng-container>
        }
      </div>
    }
  </ng-template>

  <!-- Context Menu -->
  @if (contextMenuVisible) {
    <div class="context-menu-backdrop" (click)="closeContextMenu()">
    </div>
  }
  @if (contextMenuVisible && contextMenuNode) {
    <div class="context-menu" [style.left.px]="contextMenuX"
      [style.top.px]="contextMenuY">
      <button class="context-menu-item" (click)="contextMenuRename()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        Rename
      </button>
      @if (contextMenuNode.type === 'sketch') {
        <button class="context-menu-item" (click)="contextMenuDuplicate()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          Duplicate
        </button>
      }
      @if (contextMenuNode.type === 'entity') {
        <button class="context-menu-item"
          (click)="contextMenuToggleConstruction()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 12h20" stroke-dasharray="4 2" />
          </svg>
          Toggle Construction
        </button>
      }
      @if (contextMenuNode.type === 'sketch' || contextMenuNode.type === 'entity') {
        <div class="context-menu-divider"
        ></div>
      }
      @if (contextMenuNode.type === 'sketch' || contextMenuNode.type === 'entity') {
        <button class="context-menu-item danger"
          (click)="contextMenuDelete()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Delete
        </button>
      }
    </div>
  }
</div>